import React, { useState, useMemo, useCallback } from 'react';
import { View, Text, ScrollView, RefreshControl, TouchableOpacity, Alert, Modal, Pressable, TextInput, Image, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '@/providers/AuthProvider';
import { useThemeColors } from '@/providers/ThemeProvider';
import { useChats } from '@/modules/chat';
import { ChatListItem } from '@/modules/chat/components/ChatListItem';
import { useSearchPolls, PollWithDetails } from '@/modules/polls';
import { Loader } from '@/components/Loader';
import { ChatWithDetails } from '@/modules/chat/types';
import { getChatDisplayName } from '@/modules/chat/utils/chatHelpers';
import { LinearGradient } from 'expo-linear-gradient';

export default function ChatScreen() {
  const router = useRouter();
  const colors = useThemeColors();
  const { user, activeWeekend, activeProfile } = useAuth();
  // Use activeProfile for dependent users (kind users)
  const userId = activeProfile?.id || user?.id;
  const isPlayingAsDependent = activeProfile?.id && activeProfile?.id !== user?.id;
  
  const [refreshing, setRefreshing] = useState(false);
  const [showNewChatMenu, setShowNewChatMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const {
    chats,
    loading,
    error,
    refetch,
    leaveChat,
  } = useChats(userId);

  // Poll search
  const { polls: searchedPolls, loading: pollsLoading } = useSearchPolls(userId, searchQuery);

  /**
   * Find the weekend chat (family or team chat linked to active weekend)
   */
  const weekendChat = useMemo(() => {
    if (!activeWeekend) return null;
    
    // First look for a family chat linked to this weekend
    const familyChat = chats.find(
      (c) => c.weekend_id === activeWeekend.id && (c.type === 'family' || c.type === 'team')
    );
    if (familyChat) return familyChat;
    
    // Or look for any chat that has "familie" or "weekend" in the name
    return chats.find(
      (c) => c.weekend_id === activeWeekend.id || 
             (c.type === 'group' && c.name?.toLowerCase().includes('familie'))
    );
  }, [chats, activeWeekend]);

  /**
   * Regular chats (excluding the weekend chat)
   */
  const regularChats = useMemo(() => {
    if (!weekendChat) return chats;
    return chats.filter((c) => c.id !== weekendChat.id);
  }, [chats, weekendChat]);

  /**
   * Sort chats: unread first, then by last message time
   */
  const sortedChats = useMemo(() => {
    return [...regularChats].sort((a, b) => {
      // First, sort by unread count (unread chats on top)
      const aUnread = a.unread_count || 0;
      const bUnread = b.unread_count || 0;
      
      if (aUnread > 0 && bUnread === 0) return -1;
      if (aUnread === 0 && bUnread > 0) return 1;
      
      // If both have unread or both don't, sort by last message time (newest first)
      const aTime = new Date(a.last_message_at || a.last_message?.created_at || 0).getTime();
      const bTime = new Date(b.last_message_at || b.last_message?.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [regularChats]);

  /**
   * Filter chats based on search query
   * Searches through: chat names, participant names, last message content, and dates
   */
  const filteredChats = useMemo(() => {
    if (!searchQuery.trim()) return sortedChats;

    const query = searchQuery.toLowerCase().trim();
    
    return sortedChats.filter((chat) => {
      // 1. Search in chat name
      const chatName = getChatDisplayName(chat, userId || '').toLowerCase();
      if (chatName.includes(query)) return true;

      // 2. Search in participant names
      const participantMatch = chat.participants?.some((p) => {
        const name = (p.user?.nickname || p.user?.full_name || '').toLowerCase();
        return name.includes(query);
      });
      if (participantMatch) return true;

      // 3. Search in last message content
      const lastMessageContent = (chat.last_message?.content || '').toLowerCase();
      if (lastMessageContent.includes(query)) return true;

      // 4. Search by date (format: dd-mm-yyyy, dd/mm/yyyy, or Dutch month names)
      if (chat.last_message_at || chat.last_message?.created_at) {
        const dateStr = chat.last_message_at || chat.last_message?.created_at || '';
        const date = new Date(dateStr);
        
        if (!isNaN(date.getTime())) {
          // Format date in multiple ways for matching
          const dayMonth = date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' });
          const fullDate = date.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
          const monthName = date.toLocaleDateString('nl-NL', { month: 'long' }).toLowerCase();
          const dayName = date.toLocaleDateString('nl-NL', { weekday: 'long' }).toLowerCase();
          
          if (dayMonth.includes(query) || 
              fullDate.includes(query) || 
              monthName.includes(query) ||
              dayName.includes(query)) {
            return true;
          }
        }
      }

      return false;
    });
  }, [sortedChats, searchQuery, userId]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  };

  const handleNewChat = () => {
    setShowNewChatMenu(true);
  };

  const handleNewDirectChat = () => {
    setShowNewChatMenu(false);
    router.push('/chat-screens/new-direct');
  };

  const handleNewGroupChat = () => {
    setShowNewChatMenu(false);
    router.push('/chat-screens/new-group');
  };

  const handleChatPress = (chat: ChatWithDetails) => {
    router.push(`/chat-screens/${chat.id}` as any);
  };

  const handleChatLongPress = (chat: any) => {
    const chatName = getChatDisplayName(chat, userId || '');
    const isGroup = chat.type === 'group' || chat.type === 'team';
    Alert.alert(
      chatName,
      isGroup
        ? 'Weet je zeker dat je deze groep wilt verlaten? Je ontvangt geen berichten meer.'
        : 'Weet je zeker dat je deze chat wilt verwijderen? Je ontvangt geen berichten meer.',
      [
        {
          text: isGroup ? 'Groep verlaten' : 'Chat verwijderen',
          style: 'destructive',
          onPress: async () => {
            try {
              await leaveChat(chat.id);
            } catch {
              Alert.alert('Fout', 'Kon chat niet verwijderen. Probeer het opnieuw.');
            }
          },
        },
        { text: 'Annuleer', style: 'cancel' },
      ]
    );
  };

  const handlePollPress = (poll: PollWithDetails & { chat_name?: string }) => {
    // Navigate to the chat containing this poll
    router.push(`/chat-screens/${poll.chat_id}` as any);
  };

  const formatPollStatus = (poll: PollWithDetails): string => {
    if (poll.is_closed) return 'Gesloten';
    if (poll.has_voted) return 'Gestemd';
    return 'Open';
  };

  if (!userId) {
    return (
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "left", "right"]}>
        <View className="flex-1 items-center justify-center">
          <Text style={{ color: colors.textSecondary }}>
            Please log in to view chats
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView className="flex-1" style={{ backgroundColor: colors.background }} edges={["top", "left", "right"]}>
      {/* Playing as dependent banner */}
      {isPlayingAsDependent && (
        <View className="bg-amber-500 px-4 py-2 flex-row items-center justify-center">
          <Ionicons name="person-circle-outline" size={18} color="#FFFFFF" />
          <Text className="text-white font-medium ml-2">
            Je chat als {activeProfile?.nickname || activeProfile?.full_name}
          </Text>
        </View>
      )}

      {/* Header */}
      <View className="px-6 py-4" style={{ backgroundColor: colors.surface, borderBottomWidth: 1, borderBottomColor: colors.border }}>
        <View className="flex-row items-center justify-between mb-3">
          <Text className="text-2xl font-bold" style={{ color: colors.text }}>
            Chat
          </Text>
          <TouchableOpacity
            onPress={handleNewChat}
            className="w-10 h-10 rounded-full items-center justify-center"
            style={{ backgroundColor: colors.primary }}
            activeOpacity={0.7}
          >
            <Ionicons name="add" size={24} color="#fff" />
          </TouchableOpacity>
        </View>

        {/* Search Bar */}
        <View 
          className="flex-row items-center rounded-xl px-4 py-3"
          style={{ 
            backgroundColor: colors.surfaceSecondary, 
            borderWidth: isSearchFocused ? 2 : 0,
            borderColor: isSearchFocused ? colors.primary : 'transparent'
          }}
        >
          <Ionicons 
            name="search" 
            size={20} 
            color={isSearchFocused ? colors.primary : colors.textTertiary} 
          />
          <TextInput
            value={searchQuery}
            onChangeText={setSearchQuery}
            onFocus={() => setIsSearchFocused(true)}
            onBlur={() => setIsSearchFocused(false)}
            placeholder="Zoek in chats, namen, berichten..."
            placeholderTextColor={colors.textTertiary}
            className="flex-1 ml-3 text-base"
            style={{ color: colors.text }}
            autoCapitalize="none"
            autoCorrect={false}
            returnKeyType="search"
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity
              onPress={handleClearSearch}
              activeOpacity={0.7}
              className="ml-2"
            >
              <Ionicons name="close-circle" size={20} color={colors.textTertiary} />
            </TouchableOpacity>
          )}
        </View>

        {/* Search info */}
        {searchQuery.length > 0 && (
          <View className="mt-2 flex-row items-center">
            <Ionicons name="information-circle-outline" size={14} color={colors.textTertiary} />
            <Text className="text-xs ml-1" style={{ color: colors.textSecondary }}>
              {filteredChats.length} {filteredChats.length === 1 ? 'chat' : 'chats'} gevonden
            </Text>
          </View>
        )}
      </View>

      {/* Chat List */}
      {loading && !refreshing ? (
        <View className="flex-1 items-center justify-center">
          <Loader size={60} />
        </View>
      ) : error ? (
        <View className="flex-1 items-center justify-center px-6">
          <Ionicons name="alert-circle" size={48} color="#EF4444" />
          <Text className="text-red-500 text-center mt-4">{error}</Text>
          <TouchableOpacity
            onPress={refetch}
            className="mt-4 bg-purple-600 px-6 py-3 rounded-xl"
            activeOpacity={0.7}
          >
            <Text className="text-white font-semibold">Retry</Text>
          </TouchableOpacity>
        </View>
      ) : chats.length === 0 ? (
        <View className="flex-1 items-center justify-center px-6">
          <Ionicons
            name="chatbubbles-outline"
            size={64}
            color="#9CA3AF"
          />
          <Text className="text-gray-500 dark:text-gray-400 text-center mt-4 text-lg">
            Nog geen chats
          </Text>
          <Text className="text-gray-400 dark:text-gray-500 text-center mt-2">
            Tik op de + knop om een gesprek te starten
          </Text>
        </View>
      ) : filteredChats.length === 0 && searchQuery.length > 0 ? (
        <View className="flex-1 items-center justify-center px-6">
          <Ionicons
            name="search-outline"
            size={64}
            color="#9CA3AF"
          />
          <Text className="text-gray-500 dark:text-gray-400 text-center mt-4 text-lg">
            Geen resultaten voor "{searchQuery}"
          </Text>
          <Text className="text-gray-400 dark:text-gray-500 text-center mt-2">
            Probeer een andere zoekterm
          </Text>
          <TouchableOpacity
            onPress={handleClearSearch}
            className="mt-4 bg-purple-600 px-6 py-3 rounded-xl"
            activeOpacity={0.7}
          >
            <Text className="text-white font-semibold">Wis zoekopdracht</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <ScrollView
          className="flex-1"
          contentContainerStyle={{ paddingBottom: 24 }}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              tintColor="#7C3AED"
            />
          }
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Weekend Chat Section */}
          {weekendChat && activeWeekend && !searchQuery && (
            <View className="px-6 pt-4 pb-2">
              {/* Weekend Name Header */}
              <View className="flex-row items-center mb-3">
                <Ionicons name="calendar" size={16} color="#7C3AED" />
                <Text className="text-sm font-semibold text-purple-600 dark:text-purple-400 ml-2">
                  {activeWeekend.name}
                </Text>
                {activeWeekend.theme && (
                  <Text className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                    • {activeWeekend.theme}
                  </Text>
                )}
              </View>

              <TouchableOpacity
                onPress={() => handleChatPress(weekendChat)}
                onLongPress={() => handleChatLongPress(weekendChat)}
                activeOpacity={0.9}
              >
                <LinearGradient
                  colors={['#7C3AED', '#9333EA', '#A855F7']}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  className="rounded-2xl p-4 shadow-lg"
                >
                  <View className="flex-row items-center">
                    {/* Weekend Icon */}
                    <View className="w-14 h-14 rounded-full bg-white/20 items-center justify-center mr-4">
                      <Ionicons name="chatbubbles" size={28} color="#fff" />
                    </View>

                    {/* Chat Info */}
                    <View className="flex-1">
                      <View className="flex-row items-center">
                        <Text className="text-white text-lg font-bold flex-1" numberOfLines={1}>
                          Familie Chat
                        </Text>
                        {(weekendChat.unread_count || 0) > 0 && (
                          <View className="bg-white rounded-full min-w-[24px] h-6 items-center justify-center px-2 ml-2">
                            <Text className="text-purple-600 text-xs font-bold">
                              {weekendChat.unread_count}
                            </Text>
                          </View>
                        )}
                      </View>
                      <Text className="text-purple-200 text-sm mt-0.5" numberOfLines={1}>
                        {weekendChat.last_message?.content || 'Nog geen berichten'}
                      </Text>
                      <View className="flex-row items-center mt-2">
                        {weekendChat.participants && (
                          <Text className="text-purple-200 text-xs">
                            <Ionicons name="people" size={12} color="rgba(233, 213, 255, 0.8)" /> {weekendChat.participants.length} deelnemers
                          </Text>
                        )}
                      </View>
                    </View>

                    <Ionicons name="chevron-forward" size={20} color="rgba(255,255,255,0.7)" />
                  </View>
                </LinearGradient>
              </TouchableOpacity>
            </View>
          )}

          {/* Section divider */}
          {weekendChat && activeWeekend && !searchQuery && filteredChats.length > 0 && (
            <View className="px-6 pt-6 pb-3">
              <Text className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Overige chats
              </Text>
            </View>
          )}

          {/* Poll Search Results */}
          {searchQuery.trim().length >= 2 && (
            <View className="px-6">
              {pollsLoading ? (
                <View className="py-4 items-center">
                  <ActivityIndicator size="small" color="#7C3AED" />
                </View>
              ) : searchedPolls.length > 0 ? (
                <>
                  <Text className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 mt-2">
                    Polls ({searchedPolls.length})
                  </Text>
                  {searchedPolls.map((poll) => (
                    <TouchableOpacity
                      key={poll.id}
                      onPress={() => handlePollPress(poll)}
                      className="bg-white dark:bg-gray-800 rounded-xl p-4 mb-3"
                      activeOpacity={0.7}
                    >
                      <View className="flex-row items-start">
                        <View className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900 items-center justify-center mr-3">
                          <Ionicons name="bar-chart" size={20} color="#7C3AED" />
                        </View>
                        <View className="flex-1">
                          <Text
                            className="text-base font-semibold text-gray-900 dark:text-white"
                            numberOfLines={2}
                          >
                            {poll.question}
                          </Text>
                          <View className="flex-row items-center mt-1">
                            <Text className="text-sm text-gray-500 dark:text-gray-400">
                              {poll.total_votes} {poll.total_votes === 1 ? 'stem' : 'stemmen'}
                            </Text>
                            <Text className="text-sm text-gray-400 dark:text-gray-500 mx-1">•</Text>
                            <Text className="text-sm text-purple-600 dark:text-purple-400" numberOfLines={1}>
                              {(poll as any).chat_name || 'Chat'}
                            </Text>
                          </View>
                        </View>
                        <View
                          className={`px-2 py-1 rounded-full ${
                            poll.is_closed
                              ? 'bg-gray-200 dark:bg-gray-700'
                              : poll.has_voted
                              ? 'bg-green-100 dark:bg-green-900'
                              : 'bg-purple-100 dark:bg-purple-900'
                          }`}
                        >
                          <Text
                            className={`text-xs font-medium ${
                              poll.is_closed
                                ? 'text-gray-600 dark:text-gray-400'
                                : poll.has_voted
                                ? 'text-green-700 dark:text-green-400'
                                : 'text-purple-700 dark:text-purple-400'
                            }`}
                          >
                            {formatPollStatus(poll)}
                          </Text>
                        </View>
                      </View>
                    </TouchableOpacity>
                  ))}
                </>
              ) : null}

              {/* Chats section header when searching */}
              {filteredChats.length > 0 && (
                <Text className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 mt-2">
                  Chats ({filteredChats.length})
                </Text>
              )}
            </View>
          )}

          {/* Regular Chat List */}
          <View className={`px-6 ${(!weekendChat || !activeWeekend || searchQuery) ? (searchQuery.trim().length >= 2 ? '' : 'pt-4') : ''}`}>
            {filteredChats.map((chat) => (
              <ChatListItem
                key={chat.id}
                chat={chat}
                currentUserId={userId}
                onPress={handleChatPress}
                onLongPress={handleChatLongPress}
              />
            ))}
          </View>
        </ScrollView>
      )}

      {/* New Chat Menu Modal */}
      <Modal
        visible={showNewChatMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setShowNewChatMenu(false)}
      >
        <Pressable
          className="flex-1 bg-black/50 justify-end"
          onPress={() => setShowNewChatMenu(false)}
        >
          <Pressable
            className="bg-white dark:bg-gray-800 rounded-t-3xl p-6"
            onPress={(e) => e.stopPropagation()}
          >
            <View className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full self-center mb-6" />
            
            <Text className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Nieuwe Chat
            </Text>

            <TouchableOpacity
              onPress={handleNewDirectChat}
              className="flex-row items-center py-4 border-b border-gray-100 dark:border-gray-700"
              activeOpacity={0.7}
            >
              <View className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 items-center justify-center mr-4">
                <Ionicons name="person" size={24} color="#7C3AED" />
              </View>
              <View className="flex-1">
                <Text className="text-base font-semibold text-gray-900 dark:text-white">
                  Direct Chat
                </Text>
                <Text className="text-sm text-gray-500 dark:text-gray-400">
                  Start een privégesprek met iemand
                </Text>
              </View>
              <Ionicons name="chevron-forward" size={20} color="#9CA3AF" />
            </TouchableOpacity>

            <TouchableOpacity
              onPress={handleNewGroupChat}
              className="flex-row items-center py-4"
              activeOpacity={0.7}
            >
              <View className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 items-center justify-center mr-4">
                <Ionicons name="people" size={24} color="#10B981" />
              </View>
              <View className="flex-1">
                <Text className="text-base font-semibold text-gray-900 dark:text-white">
                  Groepschat
                </Text>
                <Text className="text-sm text-gray-500 dark:text-gray-400">
                  Maak een groep met meerdere mensen
                </Text>
              </View>
              <Ionicons name="chevron-forward" size={20} color="#9CA3AF" />
            </TouchableOpacity>

            <TouchableOpacity
              onPress={() => setShowNewChatMenu(false)}
              className="mt-4 py-3 items-center"
              activeOpacity={0.7}
            >
              <Text className="text-purple-600 dark:text-purple-400 font-semibold">
                Annuleren
              </Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>
    </SafeAreaView>
  );
}
